import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Upload, Pencil, X, Map as MapIcon, Box as BoxIcon } from 'lucide-react';
import { v3Api } from '../hooks/useApi';
import { resolveAssetUrl } from '../../../config';
import FloorPlanCanvas from './FloorPlanCanvas';
import UnitEditor from './UnitEditor';
import Twin3DView from './Twin3D/Twin3DView';
import ImportPanel from './Import/ImportPanel';
import { getAssetDimensions } from './pdfRender';
import '../CommandCenter/command-center.css';
import './Import/import-panel.css';

/**
 * TwinPage — Sprint D3 floor-plan view + admin authoring.
 *
 *   view : FloorPlanCanvas overlay (KPI-colored polygons) + units table + unit
 *          drilldown.
 *   edit : admin only — upload floor plan (image/PDF) + draw/label unit polygons
 *          (normalized 0..1) and persist via PUT /api/twin/:id/units.
 */

// Twin authoring rights mirror the backend `adminTwin` gate: admin/sadmin/
// superadmin bypass by role; other roles (Super Admin, Backend User) may author
// only with the configurations permission. Gating the Edit button on the same
// rule avoids showing it to users whose save would 403.
const isAdminUser = () => {
    const role = localStorage.getItem('usertype');
    if (['admin', 'sadmin', 'superadmin'].includes(role)) return true;
    if (['Super Admin', 'Backend User'].includes(role)) return localStorage.getItem('manageConfigurations') === '1';
    return false;
};

// payload unit → editable shape
const toEditable = (u) => ({
    unit_id: u.unit_id,
    tenant_id: u.tenant_id || '',
    tenant_name: u.tenant_name || '',
    floor: u.floor ?? 1,
    zone: u.zone || '',
    size_sqft: u.size_sqft ?? 0,
    status: u.status || 'vacant',
    polygon: Array.isArray(u.polygon) ? u.polygon : [],
});

const fmtPct = (v) => (v == null || Number.isNaN(Number(v))) ? '—' : `${Number(v).toFixed(1)}%`;

export default function TwinPage() {
    const { t } = useTranslation();
    const { propertyId } = useParams();
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [admin] = useState(isAdminUser);
    const [viewMode, setViewMode] = useState('2d'); // '2d' | '3d'
    const [editing, setEditing] = useState(false);
    const [editUnits, setEditUnits] = useState([]);
    const [activeUnitId, setActiveUnitId] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const fileInputRef = useRef(null);

    const [drill, setDrill] = useState(null);        // unit detail payload
    const [drillLoading, setDrillLoading] = useState(false);

    const load = useCallback(async () => {
        if (!propertyId) return;
        setLoading(true);
        setError(null);
        try {
            const res = await v3Api.twin.property(propertyId);
            setPayload(res);
        } catch (e) {
            setError(e.response?.data?.error || e.message || t('Failed to load twin'));
        } finally {
            setLoading(false);
        }
    }, [propertyId, t]);

    useEffect(() => { load(); }, [load]);

    const property = payload?.data?.property;
    const units = payload?.data?.units || [];
    // Re-root the persisted floor-plan URL onto the current API host so the asset
    // loads whether the SPA is opened locally or from another machine via public IP.
    const floorPlan = useMemo(() => {
        const fp = property?.floor_plan;
        return fp ? { ...fp, url: resolveAssetUrl(fp.url) } : null;
    }, [property]);

    // ─── Edit-mode unit mutators ─────────────────────────────────
    const enterEdit = () => {
        setEditUnits(units.map(toEditable));
        setActiveUnitId(units[0]?.unit_id || null);
        setDirty(false);
        setSaveError(null);
        setEditing(true);
        setDrill(null);
    };
    const exitEdit = () => { setEditing(false); setActiveUnitId(null); };

    const changeUnit = (unitId, patch) => {
        setEditUnits((prev) => prev.map((u) => (u.unit_id === unitId ? { ...u, ...patch } : u)));
        if (patch.unit_id && patch.unit_id !== unitId && activeUnitId === unitId) setActiveUnitId(patch.unit_id);
        setDirty(true);
    };
    const changePolygon = (unitId, polygon) => {
        setEditUnits((prev) => prev.map((u) => (u.unit_id === unitId ? { ...u, polygon } : u)));
        setDirty(true);
    };
    const clearPolygon = (unitId) => changePolygon(unitId, []);
    const addUnit = () => {
        setEditUnits((prev) => {
            let n = prev.length + 1;
            const ids = new Set(prev.map((u) => u.unit_id));
            while (ids.has(`U-${n}`)) n++;
            const id = `U-${n}`;
            setActiveUnitId(id);
            return [...prev, { unit_id: id, tenant_id: '', tenant_name: '', floor: 1, zone: '', size_sqft: 0, status: 'vacant', polygon: [] }];
        });
        setDirty(true);
    };
    const removeUnit = (unitId) => {
        setEditUnits((prev) => prev.filter((u) => u.unit_id !== unitId));
        if (activeUnitId === unitId) setActiveUnitId(null);
        setDirty(true);
    };

    const save = async () => {
        // Client-side guard for the clearest backend rejection. A unit may be
        // saved with no polygon yet (drawn later); only a malformed partial
        // polygon (1-2 points) is rejected — matches the relaxed backend rule.
        const bad = editUnits.find((u) => Array.isArray(u.polygon) && u.polygon.length > 0 && u.polygon.length < 3);
        if (bad) { setSaveError(t('Unit {{id}} needs at least 3 points (or none).', { id: bad.unit_id })); return; }
        const dupe = editUnits.map((u) => u.unit_id).find((id, i, a) => a.indexOf(id) !== i);
        if (dupe) { setSaveError(`${t('Duplicate unit ID')}: ${dupe}`); return; }

        setSaving(true);
        setSaveError(null);
        try {
            const body = editUnits.map((u) => ({
                unit_id: u.unit_id,
                tenant_id: u.tenant_id || null,
                tenant_name: u.tenant_name || null,
                floor: Number(u.floor),
                zone: u.zone || null,
                size_sqft: Number(u.size_sqft),
                status: u.status || 'vacant',
                polygon: u.polygon,
            }));
            await v3Api.twin.saveUnits(propertyId, body);
            setDirty(false);
            await load();
        } catch (e) {
            setSaveError(e.response?.data?.error || e.message || t('Save failed'));
        } finally {
            setSaving(false);
        }
    };

    // ─── Floor-plan upload ───────────────────────────────────────
    const onPickFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setUploading(true);
        setUploadError(null);
        try {
            const dims = await getAssetDimensions(file).catch(() => ({}));
            await v3Api.twin.uploadFloorPlan(propertyId, file, dims);
            await load();
        } catch (err) {
            setUploadError(err.response?.data?.error || err.message || t('Upload failed'));
        } finally {
            setUploading(false);
        }
    };

    // ─── Unit drilldown (view) ───────────────────────────────────
    const openDrill = async (unit) => {
        setDrillLoading(true);
        setDrill({ unit_id: unit.unit_id });
        try {
            const res = await v3Api.twin.unit(propertyId, unit.unit_id);
            setDrill(res.data);
        } catch (e) {
            setDrill({ error: e.response?.data?.error || e.message });
        } finally {
            setDrillLoading(false);
        }
    };

    const canvasUnits = editing ? editUnits : units;

    return (
        <div className="v3 v3-cc">
            <div className="v3-cc-header">
                <div>
                    <h1 className="v3-cc-title">{property?.name || t('Digital Twin')}</h1>
                    <div className="v3-cc-subtitle">
                        {t('Floor plan')} · {property ? t('{{floors}} floors · {{units}} units', { floors: property.floor_count || 0, units: units.length }) : '—'}
                    </div>
                </div>
                <div className="v3-cc-actions" style={{ display: 'flex', gap: 8 }}>
                    {payload && !editing && (
                        <div className="v3-btn-group" style={{ display: 'inline-flex', border: '1px solid var(--v3-glass-border)', borderRadius: 6, overflow: 'hidden' }}>
                            <button
                                type="button"
                                className={'v3-btn v3-btn-ghost' + (viewMode === '2d' ? ' v3-btn-active' : '')}
                                onClick={() => setViewMode('2d')}
                                style={{ borderRadius: 0 }}
                            >
                                <MapIcon size={14} /> {t('2D')}
                            </button>
                            <button
                                type="button"
                                className={'v3-btn v3-btn-ghost' + (viewMode === '3d' ? ' v3-btn-active' : '')}
                                onClick={() => setViewMode('3d')}
                                style={{ borderRadius: 0 }}
                            >
                                <BoxIcon size={14} /> {t('3D')}
                            </button>
                        </div>
                    )}
                    {admin && payload && !editing && (
                        <>
                            <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,application/pdf"
                                style={{ display: 'none' }} onChange={onPickFile} />
                            <button className="v3-btn v3-btn-ghost" type="button" disabled={uploading}
                                onClick={() => fileInputRef.current?.click()}>
                                <Upload size={14} /> {uploading ? t('Uploading…') : floorPlan ? t('Replace floor plan') : t('Upload floor plan')}
                            </button>
                            <button className="v3-btn v3-btn-ghost" type="button" onClick={enterEdit}>
                                <Pencil size={14} /> {t('Edit units')}
                            </button>
                        </>
                    )}
                    {editing && (
                        <button className="v3-btn v3-btn-ghost" type="button" onClick={exitEdit}>
                            <X size={14} /> {t('Done editing')}
                        </button>
                    )}
                    <Link to={`${process.env.PUBLIC_URL}/v3/command`} className="v3-btn v3-btn-ghost" style={{ textDecoration: 'none' }}>
                        <ArrowLeft size={14} /> {t('Back to Command Center')}
                    </Link>
                </div>
            </div>

            {uploadError && <div className="v3-card v3-cc-card v3-text-danger">{t('Upload failed')}: {uploadError}</div>}
            {loading && !payload && <div className="v3-card v3-cc-card">{t('Loading twin…')}</div>}
            {error && <div className="v3-card v3-cc-card v3-text-danger">{t('Twin unavailable')}: {error}</div>}

            {payload && viewMode === '3d' && !editing && (
                <div className="v3-cc-grid">
                    <div className="cc-col-12">
                        <div className="v3-card v3-cc-card">
                            <h3>{t('Floor Plan (3D)')}</h3>
                            <Twin3DView propertyId={propertyId} payload={payload.data} />
                        </div>
                    </div>
                </div>
            )}

            {payload && (viewMode === '2d' || editing) && (
                <div className="v3-cc-grid">
                    {/* Floor-plan surface */}
                    <div className={editing ? 'cc-col-8' : 'cc-col-12'}>
                        <div className="v3-card v3-cc-card">
                            <h3>{t('Floor Plan')}</h3>
                            <FloorPlanCanvas
                                floorPlan={floorPlan}
                                units={canvasUnits}
                                mode={editing ? 'edit' : 'view'}
                                activeUnitId={activeUnitId}
                                onSelectUnit={editing ? (u) => setActiveUnitId(u.unit_id) : openDrill}
                                onPolygonChange={changePolygon}
                            />
                        </div>
                    </div>

                    {/* Editor panel */}
                    {editing && (
                        <div className="cc-col-4" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <ImportPanel
                                editUnits={editUnits}
                                setEditUnits={(next) => { setEditUnits(next); setDirty(true); }}
                                floorPlan={floorPlan}
                                onMerge={(detected) => {
                                    setEditUnits((prev) => {
                                        const byId = new Map(prev.map((u) => [u.unit_id, u]));
                                        detected.forEach((d) => {
                                            const existing = byId.get(d.unit_id);
                                            if (existing) byId.set(d.unit_id, { ...existing, polygon: d.polygon });
                                            else byId.set(d.unit_id, d);
                                        });
                                        return Array.from(byId.values());
                                    });
                                    setDirty(true);
                                }}
                            />
                            <UnitEditor
                                units={editUnits}
                                activeUnitId={activeUnitId}
                                setActiveUnitId={setActiveUnitId}
                                onAddUnit={addUnit}
                                onChangeUnit={changeUnit}
                                onRemoveUnit={removeUnit}
                                onClearPolygon={clearPolygon}
                                onSave={save}
                                saving={saving}
                                saveError={saveError}
                                dirty={dirty}
                            />
                        </div>
                    )}

                    {/* Drilldown (view) */}
                    {!editing && drill && (
                        <div className="cc-col-12">
                            <div className="v3-card v3-cc-card">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <h3 style={{ margin: 0 }}>{t('Unit')} {drill.unit?.unit_id || drill.unit_id}</h3>
                                    <button className="v3-btn v3-btn-ghost" type="button" onClick={() => setDrill(null)}><X size={14} /></button>
                                </div>
                                {drillLoading && <div className="v3-text-muted" style={{ fontSize: 12 }}>{t('Loading…')}</div>}
                                {drill.error && <div className="v3-text-danger" style={{ fontSize: 12 }}>{drill.error}</div>}
                                {drill.kpis && (
                                    <div className="v3-cc-twin" style={{ marginTop: 8 }}>
                                        <div className="meta-row">
                                            <div><span className="meta-label">{t('Tenant')}</span><strong>{drill.unit?.tenant_name || drill.unit?.tenant_id || '—'}</strong></div>
                                            <div><span className="meta-label">{t('OCR')}</span><strong>{fmtPct(drill.kpis.ocr_pct)}</strong></div>
                                            <div><span className="meta-label">{t('YoY')}</span><strong>{fmtPct(drill.kpis.yoy_growth_pct)}</strong></div>
                                            <div><span className="meta-label">{t('Anomalies')}</span><strong>{drill.kpis.anomaly_count || 0}</strong></div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Units table (kept as data grid / fallback) */}
                    <div className="cc-col-12">
                        <div className="v3-card v3-cc-card">
                            <h3>{t('Units ({{count}})', { count: units.length })}</h3>
                            {units.length === 0 ? (
                                <div className="v3-text-muted" style={{ padding: '12px 0' }}>
                                    {admin ? t('No units registered — use “Edit units” to draw them on the floor plan.') : t('No units registered for this property.')}
                                </div>
                            ) : (
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                        <thead>
                                            <tr style={{ textAlign: 'left', color: 'var(--v3-text-muted)', borderBottom: '1px solid var(--v3-glass-border)' }}>
                                                <th style={{ padding: '8px 6px' }}>{t('Unit')}</th>
                                                <th style={{ padding: '8px 6px' }}>{t('Tenant')}</th>
                                                <th style={{ padding: '8px 6px' }}>{t('Floor')}</th>
                                                <th style={{ padding: '8px 6px' }}>{t('Size (sqft)')}</th>
                                                <th style={{ padding: '8px 6px' }}>{t('Status')}</th>
                                                <th style={{ padding: '8px 6px' }}>{t('OCR %')}</th>
                                                <th style={{ padding: '8px 6px' }}>{t('YoY %')}</th>
                                                <th style={{ padding: '8px 6px' }}>{t('Anomalies')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {units.map((u) => (
                                                <tr key={u.unit_id} style={{ borderBottom: '1px solid var(--v3-glass-border)', cursor: 'pointer' }}
                                                    onClick={() => !editing && openDrill(u)}>
                                                    <td style={{ padding: '8px 6px', fontWeight: 600 }}>{u.unit_id}</td>
                                                    <td style={{ padding: '8px 6px' }}>{u.tenant_name || u.tenant_id || '—'}</td>
                                                    <td style={{ padding: '8px 6px' }}>{u.floor ?? '—'}</td>
                                                    <td style={{ padding: '8px 6px' }}>{u.size_sqft ?? '—'}</td>
                                                    <td style={{ padding: '8px 6px' }}>{u.status || '—'}</td>
                                                    <td style={{ padding: '8px 6px' }}>{fmtPct(u.kpis?.ocr_pct)}</td>
                                                    <td style={{ padding: '8px 6px' }}>{fmtPct(u.kpis?.yoy_growth_pct)}</td>
                                                    <td style={{ padding: '8px 6px' }}>
                                                        {u.kpis?.anomaly_count > 0 ? (
                                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--v3-text-danger, #c0392b)' }}>
                                                                <AlertTriangle size={12} />{u.kpis.anomaly_count}
                                                            </span>
                                                        ) : '0'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
